import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    TextField,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Paper,
    IconButton,
    Tooltip,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from "@mui/material";
import {
    PlayArrow as PlayArrowIcon,
    Close as CloseIcon,
    CheckCircle as CheckCircleIcon,
    Error as ErrorIcon,
    Send as SendIcon,
    ContentCopy as CopyIcon,
    Refresh as RefreshIcon,
    Lock as LockIcon,
    ExpandMore as ExpandMoreIcon,
    Visibility,
    VisibilityOff,
} from "@mui/icons-material";
import Api from "../../services/api";
import { PapiroConsole } from "../../utils/papiroConsole";

const AUTH_TYPES = [
    { value: "NONE", label: "Sem autenticação" },
    { value: "BASIC", label: "Basic Auth" },
    { value: "BEARER", label: "Bearer Token" },
    { value: "API_KEY", label: "API Key" },
    { value: "OAUTH2_CLIENT_CREDENTIALS", label: "OAuth 2.0 (Client Credentials)" },
];

const styles = {
    dialogTitle: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #e0e0e0",
    },
    codeBlock: {
        backgroundColor: "#1e1e1e",
        color: "#d4d4d4",
        fontFamily: "monospace",
        fontSize: "12px",
        padding: "12px",
        borderRadius: "4px",
        overflow: "auto",
        maxHeight: "180px",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
    },
    responseHeader: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "8px",
    },
    statusChip: {
        fontWeight: 600,
    },
    stepContent: {
        paddingTop: "8px",
        paddingBottom: "16px",
    },
    infoBox: {
        backgroundColor: "#f5f5f5",
        padding: "12px",
        borderRadius: "4px",
        marginBottom: "12px",
    },
    copyButton: {
        position: "absolute",
        top: "4px",
        right: "20px",
    },
    codeContainer: {
        position: "relative",
    },
};

export default function WebhookTestModalAutomation({
    open,
    onClose,
    webhookId,
    payloadFormat,
    payloadTemplate,
    webhookName,
    webhookUrl,
    webhookAuthType,
    webhookAuthConfig,
}) {
    const [activeStep, setActiveStep] = useState(0);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [testError, setTestError] = useState(null);
    const [sampleCalledId, setSampleCalledId] = useState("");
    const [resolvedPayload, setResolvedPayload] = useState("");

    // Estado de autenticação para o teste
    const [testAuthType, setTestAuthType] = useState("NONE");
    const [testAuthConfig, setTestAuthConfig] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [showToken, setShowToken] = useState(false);
    const [testEndpointUrl, setTestEndpointUrl] = useState("");

    // Reset state quando o modal abre
    useEffect(() => {
        if (open) {
            setActiveStep(0);
            setTestResult(null);
            setTestError(null);
            setSampleCalledId("");
            setResolvedPayload("");
            setShowPassword(false);
            setShowToken(false);
            setTestEndpointUrl(webhookUrl || "");

            // Pre-fill auth type e campos não-sensíveis do webhook
            const authType = webhookAuthType || "NONE";
            setTestAuthType(authType);

            if (webhookAuthConfig && authType !== "NONE") {
                // Normalizar keys para camelCase antes de copiar
                // O backend pode retornar keys lowercase (clientid, tokenurl) por causa do LowercaseCaseNamingPolicy
                const keyMap = {
                    clientid: 'clientId', client_id: 'clientId',
                    clientsecret: 'clientSecret', client_secret: 'clientSecret',
                    tokenurl: 'tokenUrl', token_url: 'tokenUrl',
                    granttype: 'grantType', grant_type: 'grantType',
                    headername: 'headerName', header_name: 'headerName',
                    apikey: 'apiKey', api_key: 'apiKey',
                    usebasicauthheader: 'useBasicAuthHeader',
                };
                const normalized = {};
                Object.entries(webhookAuthConfig).forEach(([key, value]) => {
                    const normalizedKey = keyMap[key] || key;
                    normalized[normalizedKey] = value;
                });
                const safeCfg = {};
                // Copiar apenas campos não-sensíveis (pre-fill)
                if (normalized.username) safeCfg.username = normalized.username;
                if (normalized.headerName) safeCfg.headerName = normalized.headerName;
                if (normalized.clientId) safeCfg.clientId = normalized.clientId;
                if (normalized.tokenUrl) safeCfg.tokenUrl = normalized.tokenUrl;
                if (normalized.scope) safeCfg.scope = normalized.scope;
                if (normalized.audience) safeCfg.audience = normalized.audience;
                // Campos sensíveis ficam vazios: password, token, apiKey, clientSecret
                setTestAuthConfig(safeCfg);
            } else {
                setTestAuthConfig({});
            }
        }
    }, [open, webhookAuthType, webhookAuthConfig]);

    const updateAuthConfig = (field, value) => {
        setTestAuthConfig(prev => ({ ...prev, [field]: value }));
    };

    // Handler para executar teste
    const handleTest = async () => {
        if (!webhookId) {
            setTestError("Selecione um webhook para testar.");
            return;
        }

        if (!payloadTemplate) {
            setTestError("Defina um template de payload primeiro.");
            return;
        }

        // Validar sintaxe JSON antes de enviar (apenas para formato JSON)
        if (payloadFormat === "CUSTOM_JSON" || !payloadFormat) {
            try {
                JSON.parse(payloadTemplate);
            } catch (e) {
                setTestError("JSON inválido: " + e.message);
                return;
            }
        }

        setTesting(true);
        setTestError(null);
        setTestResult(null);
        setActiveStep(1);

        try {
            const requestBody = {
                webhookId: webhookId,
                payloadTemplate: payloadTemplate,
                payloadFormat: payloadFormat || "CUSTOM_JSON",
                sampleCalledId: sampleCalledId ? parseInt(sampleCalledId) : null,
                endpointUrl: testEndpointUrl || null,
            };

            // Enviar auth override SOMENTE se o usuario preencheu credenciais sensiveis
            // Se nao preencheu, nao envia → backend usa auth armazenada/criptografada do webhook
            if (testAuthType && testAuthType !== "NONE") {
                const hasSensitiveField = testAuthConfig.password || testAuthConfig.token
                    || testAuthConfig.apiKey || testAuthConfig.clientSecret;
                if (hasSensitiveField) {
                    requestBody.authType = testAuthType;
                    requestBody.authConfig = testAuthConfig;
                }
            }

            const response = await Api.post("/automation/webhook/test", requestBody);

            if (response.data?.success) {
                setTestResult(response.data.data);
                setResolvedPayload(response.data.data?.processedpayload || "");
                setActiveStep(2);
            } else {
                setTestError(response.data?.errors?.join(", ") || "Erro ao testar webhook");
                setActiveStep(2);
            }
        } catch (err) {
            PapiroConsole.log("Erro ao testar webhook:", err);
            setTestError(err.response?.data?.errors?.join(", ") || "Erro ao testar webhook. Verifique a conexão.");
            setActiveStep(2);
        } finally {
            setTesting(false);
        }
    };

    // Copiar para clipboard
    const handleCopy = (text) => {
        navigator.clipboard.writeText(text);
    };

    // Obter cor do status HTTP
    const getStatusColor = (statusCode) => {
        if (statusCode >= 200 && statusCode < 300) return "success";
        if (statusCode >= 400 && statusCode < 500) return "warning";
        if (statusCode >= 500) return "error";
        return "default";
    };

    // Formatar tempo de resposta
    const formatResponseTime = (ms) => {
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(2)}s`;
    };

    const steps = [
        {
            label: "Configuração",
            description: "Configure os parâmetros do teste",
        },
        {
            label: "Executando",
            description: "Enviando requisição para o webhook",
        },
        {
            label: "Resultado",
            description: "Analise a resposta do webhook",
        },
    ];

    const renderAuthFields = () => {
        if (testAuthType === "NONE") return null;

        return (
            <Box sx={{ mt: 2 }}>
                {testAuthType === "BASIC" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Usuário"
                                fullWidth
                                size="small"
                                value={testAuthConfig.username || ""}
                                onChange={(e) => updateAuthConfig("username", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Senha"
                                fullWidth
                                size="small"
                                type={showPassword ? "text" : "password"}
                                value={testAuthConfig.password || ""}
                                onChange={(e) => updateAuthConfig("password", e.target.value)}
                                placeholder="Informe a senha para o teste"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                )}

                {testAuthType === "BEARER" && (
                    <TextField
                        label="Bearer Token"
                        fullWidth
                        size="small"
                        type={showToken ? "text" : "password"}
                        value={testAuthConfig.token || ""}
                        onChange={(e) => updateAuthConfig("token", e.target.value)}
                        placeholder="Informe o token para o teste"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setShowToken(!showToken)}>
                                        {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                )}

                {testAuthType === "API_KEY" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Nome do Header"
                                fullWidth
                                size="small"
                                value={testAuthConfig.headerName || ""}
                                onChange={(e) => updateAuthConfig("headerName", e.target.value)}
                                placeholder="Ex: X-API-Key"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="API Key"
                                fullWidth
                                size="small"
                                type={showToken ? "text" : "password"}
                                value={testAuthConfig.apiKey || ""}
                                onChange={(e) => updateAuthConfig("apiKey", e.target.value)}
                                placeholder="Informe a API Key para o teste"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowToken(!showToken)}>
                                                {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                    </Grid>
                )}

                {testAuthType === "OAUTH2_CLIENT_CREDENTIALS" && (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Client ID"
                                fullWidth
                                size="small"
                                value={testAuthConfig.clientId || ""}
                                onChange={(e) => updateAuthConfig("clientId", e.target.value)}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Client Secret"
                                fullWidth
                                size="small"
                                type={showPassword ? "text" : "password"}
                                value={testAuthConfig.clientSecret || ""}
                                onChange={(e) => updateAuthConfig("clientSecret", e.target.value)}
                                placeholder="Informe o client secret para o teste"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Token URL"
                                fullWidth
                                size="small"
                                value={testAuthConfig.tokenUrl || ""}
                                onChange={(e) => updateAuthConfig("tokenUrl", e.target.value)}
                                placeholder="https://auth.example.com/oauth/token"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Scope (opcional)"
                                fullWidth
                                size="small"
                                value={testAuthConfig.scope || ""}
                                onChange={(e) => updateAuthConfig("scope", e.target.value)}
                                placeholder="Ex: read write"
                            />
                        </Grid>
                    </Grid>
                )}

                {webhookAuthType && webhookAuthType !== "NONE" && (
                    <Alert severity="info" sx={{ mt: 1, py: 0, fontSize: "12px" }}>
                        {testAuthType === "BASIC" && "A senha não é exibida por segurança. Informe-a para executar o teste."}
                        {testAuthType === "BEARER" && "O token não é exibido por segurança. Informe-o para executar o teste."}
                        {testAuthType === "API_KEY" && "A API Key não é exibida por segurança. Informe-a para executar o teste."}
                        {testAuthType === "OAUTH2_CLIENT_CREDENTIALS" && "O client secret não é exibido por segurança. Informe-o para executar o teste."}
                    </Alert>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={styles.dialogTitle}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PlayArrowIcon color="primary" />
                    <Typography variant="h6">Testar Webhook</Typography>
                </Box>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
                {/* Info do Webhook */}
                <Box sx={styles.infoBox}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary">
                                Webhook
                            </Typography>
                            <Typography variant="body2" fontWeight={500}>
                                {webhookName || "Não selecionado"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={5}>
                            <Typography variant="caption" color="text.secondary">
                                URL
                            </Typography>
                            <Typography variant="body2" noWrap title={webhookUrl}>
                                {webhookUrl || "-"}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="caption" color="text.secondary">
                                Autenticação
                            </Typography>
                            <Typography variant="body2">
                                <Chip
                                    size="small"
                                    label={AUTH_TYPES.find(t => t.value === (webhookAuthType || "NONE"))?.label || webhookAuthType || "Nenhuma"}
                                    color={webhookAuthType && webhookAuthType !== "NONE" ? "primary" : "default"}
                                    variant="outlined"
                                    icon={<LockIcon fontSize="small" />}
                                />
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>

                {/* Stepper */}
                <Stepper activeStep={activeStep} orientation="vertical">
                    {/* Step 1: Configuração */}
                    <Step>
                        <StepLabel>{steps[0].label}</StepLabel>
                        <StepContent>
                            <Box sx={styles.stepContent}>
                                <TextField
                                    label="URL do Endpoint"
                                    fullWidth
                                    size="small"
                                    value={testEndpointUrl}
                                    onChange={(e) => setTestEndpointUrl(e.target.value)}
                                    sx={{ mb: 2 }}
                                />

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Opcionalmente, informe um ID de chamado para usar dados reais no teste.
                                    Se não informar, serão usados dados de exemplo.
                                </Typography>

                                <TextField
                                    label="ID do Chamado (opcional)"
                                    value={sampleCalledId}
                                    onChange={(e) => setSampleCalledId(e.target.value.replace(/\D/g, ""))}
                                    placeholder="Ex: 12345"
                                    size="small"
                                    sx={{ width: 200 }}
                                    helperText="Deixe vazio para usar dados de exemplo"
                                />

                                {/* Seção de Autenticação */}
                                <Divider sx={{ my: 2 }} />
                                <Accordion
                                    defaultExpanded={webhookAuthType && webhookAuthType !== "NONE"}
                                    sx={{ boxShadow: "none", border: "1px solid #e0e0e0", "&:before": { display: "none" } }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <LockIcon fontSize="small" color={testAuthType !== "NONE" ? "primary" : "disabled"} />
                                            <Typography variant="subtitle2">
                                                Autenticação
                                            </Typography>
                                            {testAuthType !== "NONE" && (
                                                <Chip size="small" label={AUTH_TYPES.find(t => t.value === testAuthType)?.label} color="primary" variant="outlined" />
                                            )}
                                        </Box>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                            <InputLabel>Tipo de Autenticação</InputLabel>
                                            <Select
                                                value={testAuthType}
                                                label="Tipo de Autenticação"
                                                onChange={(e) => {
                                                    setTestAuthType(e.target.value);
                                                    if (e.target.value === "NONE") setTestAuthConfig({});
                                                }}
                                            >
                                                {AUTH_TYPES.map(at => (
                                                    <MenuItem key={at.value} value={at.value}>{at.label}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                        {renderAuthFields()}
                                    </AccordionDetails>
                                </Accordion>

                                <Divider sx={{ my: 2 }} />

                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Template do Payload:
                                </Typography>
                                <Box sx={styles.codeContainer}>
                                    <Box sx={styles.codeBlock}>
                                        {payloadTemplate || "Nenhum template definido"}
                                    </Box>
                                    <Tooltip title="Copiar">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleCopy(payloadTemplate)}
                                            sx={styles.copyButton}
                                        >
                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        </StepContent>
                    </Step>

                    {/* Step 2: Executando */}
                    <Step>
                        <StepLabel>{steps[1].label}</StepLabel>
                        <StepContent>
                            <Box sx={{ ...styles.stepContent, textAlign: "center", py: 4 }}>
                                <CircularProgress size={40} />
                                <Typography variant="body1" sx={{ mt: 2 }}>
                                    Enviando requisição...
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Aguarde enquanto o webhook é executado
                                </Typography>
                            </Box>
                        </StepContent>
                    </Step>

                    {/* Step 3: Resultado */}
                    <Step>
                        <StepLabel
                            error={!!testError}
                            optional={
                                testResult && testResult.statuscode != null && (
                                    <Chip
                                        size="small"
                                        label={`${testResult.statuscode}`}
                                        color={getStatusColor(testResult.statuscode)}
                                        sx={styles.statusChip}
                                    />
                                )
                            }
                        >
                            {steps[2].label}
                        </StepLabel>
                        <StepContent>
                            <Box sx={styles.stepContent}>
                                {testError && (
                                    <Alert severity="error" sx={{ mb: 2 }}>
                                        {testError}
                                    </Alert>
                                )}

                                {testResult && (
                                    <>
                                        {/* Status e Tempo */}
                                        <Box sx={styles.responseHeader}>
                                            {testResult.success ? (
                                                <CheckCircleIcon color="success" />
                                            ) : (
                                                <ErrorIcon color="error" />
                                            )}
                                            <Typography variant="body1" fontWeight={500}>
                                                {testResult.success ? "Sucesso" : "Falha"}
                                            </Typography>
                                            {testResult.statuscode != null && (
                                                <Chip
                                                    size="small"
                                                    label={`HTTP ${testResult.statuscode}`}
                                                    color={getStatusColor(testResult.statuscode)}
                                                />
                                            )}
                                            {testResult.responsetimems != null && (
                                                <Chip
                                                    size="small"
                                                    label={formatResponseTime(testResult.responsetimems)}
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>

                                        {/* Payload Enviado */}
                                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                            Payload Enviado:
                                        </Typography>
                                        <Box sx={styles.codeContainer}>
                                            <Box sx={styles.codeBlock}>
                                                {resolvedPayload || testResult.processedpayload || "-"}
                                            </Box>
                                            <Tooltip title="Copiar">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleCopy(resolvedPayload || testResult.processedpayload)}
                                                    sx={styles.copyButton}
                                                >
                                                    <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>

                                        {/* Request Headers */}
                                        {testResult.requestheaders && Object.keys(testResult.requestheaders).length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Headers Enviados:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {Object.entries(testResult.requestheaders).map(([key, value]) => (
                                                            <div key={key}><strong>{key}:</strong> {value}</div>
                                                        ))}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(JSON.stringify(testResult.requestheaders, null, 2))}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Response Headers */}
                                        {testResult.responseheaders && Object.keys(testResult.responseheaders).length > 0 && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Headers da Resposta:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {Object.entries(testResult.responseheaders).map(([key, value]) => (
                                                            <div key={key}><strong>{key}:</strong> {value}</div>
                                                        ))}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(JSON.stringify(testResult.responseheaders, null, 2))}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Response Body */}
                                        {testResult.responsebody && (
                                            <>
                                                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                                                    Resposta do Servidor:
                                                </Typography>
                                                <Box sx={styles.codeContainer}>
                                                    <Box sx={styles.codeBlock}>
                                                        {testResult.responsebody}
                                                    </Box>
                                                    <Tooltip title="Copiar">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleCopy(testResult.responsebody)}
                                                            sx={styles.copyButton}
                                                        >
                                                            <CopyIcon fontSize="small" sx={{ color: "#fff" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </>
                                        )}

                                        {/* Error Message */}
                                        {testResult.errormessage && (
                                            <Alert severity="warning" sx={{ mt: 2 }}>
                                                {testResult.errormessage}
                                            </Alert>
                                        )}
                                    </>
                                )}
                            </Box>
                        </StepContent>
                    </Step>
                </Stepper>
                </div>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0" }}>
                <Button onClick={onClose} color="inherit">
                    Fechar
                </Button>

                {activeStep === 0 && (
                    <Button
                        variant="contained"
                        onClick={handleTest}
                        disabled={testing || !webhookId}
                        startIcon={testing ? <CircularProgress size={16} /> : <SendIcon />}
                    >
                        Executar Teste
                    </Button>
                )}

                {activeStep === 2 && (
                    <Button
                        variant="outlined"
                        onClick={() => {
                            setActiveStep(0);
                            setTestResult(null);
                            setTestError(null);
                        }}
                        startIcon={<RefreshIcon />}
                    >
                        Testar Novamente
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
